import { useEffect } from 'react';

import { normalizeUserTourAttributes } from './attributes';
import installUserTourStub from './loader';
import useUserTourBannerOffset from './useUserTourBannerOffset';

export { USER_TOUR_ACTING_AS_KEYS, USER_TOUR_ATTRIBUTE_KEYS, normalizeUserTourAttributes } from './attributes';

export interface UserTourConfig {
	token: string;
	/** Self-hosted User Tour origin, e.g. https://tours.example.com */
	host: string;
	/** The real (logged-in) person, also while acting as another user; omit only when unknown. */
	userId?: string;
	attributes?: Record<string, unknown>;
}

interface UserTourApi {
	_stubbed?: boolean;
	load?(): Promise<void>;
	init(token: string): void;
	identify(userId: string, attributes?: Record<string, unknown>): Promise<void>;
	reset(): void;
	start(contentId: string, options?: { once?: boolean; continue?: boolean }): Promise<void>;
	[method: string]: unknown;
}

declare global {
	interface Window {
		BAY_USERTOUR?: UserTourConfig;
		// On window, not module scope, so every bundle that embeds bay-ui shares it
		BAY_USERTOUR_STATE?: { userId?: string };
		USERTOURJS_ENV_VARS?: Record<string, string>;
		usertour?: UserTourApi;
	}
}

// Mounted by both TopNav and EditorHeader: inits once per page, then re-identifies only when userId changes.
export const startUserTour = (config: UserTourConfig | undefined = window.BAY_USERTOUR): boolean => {
	if (!config?.token || !config?.host) return false;

	const userId = config.userId || undefined;
	const state = window.BAY_USERTOUR_STATE;
	if (state && state.userId === userId) return false;

	if (!state) {
		const root = config.host.replace(/\/+$/, '');
		window.USERTOURJS_ENV_VARS = {
			WS_URI: `${root}/`,
			ASSETS_URI: `${root}/sdk/`,
			USERTOURJS_ES2020_URL: `${root}/sdk/es2020/usertour.js`,
			USERTOURJS_LEGACY_URL: `${root}/sdk/legacy/usertour.iife.js`,
		};
		installUserTourStub();
	}

	const usertour = window.usertour as UserTourApi;
	// The stub drops load()'s promise, so a failed SDK load is otherwise an unhandled rejection; loader.js already warns
	if (usertour._stubbed) void usertour.load?.().catch(() => {});

	if (!state) usertour.init(config.token);
	// New user or sign-out without a page reload (SPA logout and login)
	else if (state.userId) usertour.reset();

	window.BAY_USERTOUR_STATE = { userId };
	if (userId) {
		void usertour
			.identify(userId, normalizeUserTourAttributes(config.attributes))
			.catch((error: unknown) => console.warn('User Tour identify failed', error));
	}
	return true;
};

interface BayUserTourProps {
	/** Falls back to window.BAY_USERTOUR, which the host's server layout prints. */
	config?: UserTourConfig;
}

const BayUserTour = ({ config }: BayUserTourProps) => {
	useUserTourBannerOffset();
	useEffect(() => {
		startUserTour(config ?? window.BAY_USERTOUR);
	}, [config]);

	return null;
};

export default BayUserTour;
